import { isExcludedByResolvedUsers, useResolvedUsers } from '@src/hooks/filters/useUsersFilter';
import { useUser } from '@src/hooks/useUser';
import {
  FilterClauseType,
  IconName,
  cleanupInitialFilterDateRange,
  filterExistsAndExcludes,
  removeEmptyFilters,
  useDateRangeFilter,
  type BlotterTableClientLocalFilter,
  type BlotterTableFilter,
  type BlotterTableFiltersProps,
  type DateRangeFilter,
  type FilterClause,
  type FilterableProperty,
  type UseFilterBuilderProps,
  type UsePersistedBlotterTable,
} from '@talos/kyoko';
import { isEqual, pick } from 'lodash-es';
import { useCallback, useMemo, useState, type SetStateAction } from 'react';
import { useUsersFilter } from '../../../hooks';
import type {
  AppNotification,
  AppNotificationOrderType,
  AppNotificationPriority,
} from '../../../providers/AppNotifications/AppNotificationsMonitorContainer';
import type { NotificationWithToastInfo } from './useEnrichNotificationsPipe';

const NOTIFICATION_SEVERITIES = ['High', 'Medium', 'Low'] as const satisfies readonly AppNotificationPriority[];
const NOTIFICATION_TYPES = ['OrderStatus'] as const satisfies readonly AppNotificationOrderType['Type'][];
const NOTIFICATION_STATES = ['New', 'Archived', 'Withdrawn'] as const satisfies readonly AppNotification['State'][];

export interface NotificationsBlotterTableFilter extends DateRangeFilter {
  Severities?: AppNotificationPriority[];
  Types?: string[];
  States?: string[];
  Users?: string[];
}

interface UseNotificationsFilterProps {
  persistedBlotterTable: UsePersistedBlotterTable;
}

export const useNotificationsFilter = (props: UseNotificationsFilterProps) => {
  const { persistedBlotterTable } = props;
  const { initialState, onFilterChanged: saveFilter } = persistedBlotterTable;
  const { Name: currentUserName } = useUser();
  const [filter, setFilter] = useState<NotificationsBlotterTableFilter>(() =>
    cleanupInitialFilterDateRange(initialState?.filter)
  );

  const resolvedUsers = useResolvedUsers(filter.Users, currentUserName);

  const clientSideFilter = useCallback<BlotterTableClientLocalFilter<NotificationWithToastInfo>>(
    row => {
      const data = row.data;

      if (!data) {
        return false;
      }

      if (filterExistsAndExcludes(filter, 'Severities', data.notification, 'Severity')) {
        return false;
      }
      if (filterExistsAndExcludes(filter, 'Types', data.notification, 'Type')) {
        return false;
      }
      if (filterExistsAndExcludes(filter, 'States', data.notification, 'State')) {
        return false;
      }
      if (isExcludedByResolvedUsers(resolvedUsers, data.notification?.User)) {
        return false;
      }
      return true;
    },
    [filter, resolvedUsers]
  );

  const usersFilter = useUsersFilter();
  const filterableProperties: FilterableProperty[] = useMemo(
    () => [
      {
        key: 'Severities',
        label: 'Severity',
        control: 'multi-select',
        icon: IconName.Annotation,
        options: [...NOTIFICATION_SEVERITIES],
        getOptionLabel: (option: string) => option,
      },
      {
        key: 'Types',
        label: 'Type',
        control: 'multi-select',
        icon: IconName.AtSymbol,
        options: [...NOTIFICATION_TYPES],
        getOptionLabel: (option: string) => option,
      },
      {
        key: 'States',
        label: 'State',
        control: 'multi-select',
        icon: IconName.CheckCircle,
        options: [...NOTIFICATION_STATES],
        getOptionLabel: (option: string) => option,
      },
      usersFilter,
    ],
    [usersFilter]
  );

  const initialFilterClauses = useMemo(() => {
    const clauses: FilterClause[] = [];
    if (filter) {
      const clauseKeys: (keyof NotificationsBlotterTableFilter)[] = ['Severities', 'Types', 'States'];
      clauseKeys.forEach(key => {
        clauses.push({
          key,
          type: FilterClauseType.INCLUSIVE,
          selections: (filter[key] ?? []) as string[],
        });
      });
    }
    return clauses;
  }, [filter]);

  const changeFilter = useCallback(
    (action: SetStateAction<BlotterTableFilter>) => {
      const priorFilter = filter;
      const newFilter = action instanceof Function ? action(filter) : action;

      if (!isEqual(priorFilter, newFilter)) {
        setFilter(newFilter);
        saveFilter(newFilter);
      }
    },
    [filter, saveFilter]
  );

  const handleFilterClausesChanged = useCallback(
    (filterClausesByPropertyKey: Map<string, FilterClause>, propertiesByKey: Map<string, FilterableProperty>) => {
      changeFilter(curr => {
        const newFilter = removeEmptyFilters<NotificationsBlotterTableFilter>({
          ...curr,
          ...(Object.fromEntries(
            [...propertiesByKey.keys()].map(key => [key, filterClausesByPropertyKey.get(key)?.selections])
          ) as unknown as NotificationsBlotterTableFilter),
        });
        if (isEqual(curr, newFilter)) {
          return curr;
        }
        return newFilter;
      });
    },
    [changeFilter]
  );

  const dateRangeFilter = useDateRangeFilter(filter, changeFilter);

  const filterBuilderProps = useMemo(
    () => ({
      initialFilterClauses,
      properties: filterableProperties,
      onFilterClausesChanged: handleFilterClausesChanged,
    }),
    [filterableProperties, handleFilterClausesChanged, initialFilterClauses]
  ) satisfies UseFilterBuilderProps;

  const blotterTableFilterProps = useMemo(
    () => ({
      ...dateRangeFilter,
    }),
    [dateRangeFilter]
  ) satisfies Partial<BlotterTableFiltersProps>;

  return {
    initialFilter: initialState?.filter,
    filter,
    clientSideFilter,
    changeFilter,
    filterBuilderProps,
    blotterTableFilterProps,
  };
};

export function colIDToFilterBuilderKey(id: string): keyof NotificationsBlotterTableFilter | undefined {
  switch (id) {
    case 'toastInfo.variant':
      return 'Severities';
    case 'toastInfo.appInboxToastType':
      return 'Types';
    case 'notification.State':
      return 'States';
  }

  return undefined;
}

export function onlyServerFilterKeys(filter?: NotificationsBlotterTableFilter) {
  if (filter == null) {
    return filter;
  }
  return pick(filter, ['StartDate', 'EndDate']);
}
